<template>
    <v-row justify="center" align="center">
        <v-col cols="12" sm="12" md="12">
            <v-row>
                <v-col cols="12" sm="12" md="12">
                    <h1>Product Details</h1>
                </v-col>
            </v-row>
            <alert-message ref="errorAlert" :message="data.errorAlertMessage"></alert-message>

            <!-- Delete Product Cost Confirmation Dialog -->
            <v-dialog v-model="data.showDeleteProductCostDialog" max-width="500px">
                <v-card>
                    <v-card-title class="text-h5"> Confirm Deletion </v-card-title>
                    <v-card-text>
                        Are you sure you want to delete this product cost record?
                        <br /><br />
                        <strong>Cost Amount:</strong> {{ data.productCostToDelete?.productCostAmount || 'N/A' }}
                        <br />
                        <strong>Effective Date:</strong>
                        {{ formatDateTime(data.productCostToDelete?.dateEffectiveOn) || 'N/A' }} <br /><br />
                        This action cannot be undone.
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn color="grey darken-1" text @click="cancelDeleteProductCost"> Cancel </v-btn>
                        <v-btn color="red darken-1" text @click="confirmDeleteProductCost"> Delete </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>

            <!-- Delete Shipping Fee Confirmation Dialog -->
            <v-dialog v-model="data.showDeleteShippingFeeDialog" max-width="500px">
                <v-card>
                    <v-card-title class="text-h5"> Confirm Deletion </v-card-title>
                    <v-card-text>
                        Are you sure you want to delete this estimated shipping fee record?
                        <br /><br />
                        <strong>Fee Amount:</strong> {{ data.shippingFeeToDelete?.estimatedShippingFeeAmount || 'N/A' }}
                        <br />
                        <strong>Effective Date:</strong>
                        {{ formatDateTime(data.shippingFeeToDelete?.dateEffectiveOn) || 'N/A' }} <br /><br />
                        This action cannot be undone.
                    </v-card-text>
                    <v-card-actions>
                        <v-spacer></v-spacer>
                        <v-btn color="grey darken-1" text @click="cancelDeleteShippingFee"> Cancel </v-btn>
                        <v-btn color="red darken-1" text @click="confirmDeleteShippingFee"> Delete </v-btn>
                    </v-card-actions>
                </v-card>
            </v-dialog>

            <!-- Product Details Section -->
            <v-card class="mb-6">
                <v-card-title>
                    <h2>Product Information</h2>
                </v-card-title>
                <v-card-text>
                    <v-row v-if="data.product">
                        <v-col cols="12" sm="6" md="4">
                            <v-text-field v-model="data.product.sku" :label="formLabelText.sku"></v-text-field>
                        </v-col>
                        <v-col cols="12" sm="6" md="8">
                            <v-text-field v-model="data.product.title" :label="formLabelText.title"></v-text-field>
                        </v-col>
                        <v-col cols="12" sm="12" md="12">
                            <v-textarea
                                v-model="data.product.description"
                                :label="formLabelText.description"
                                rows="4"
                            ></v-textarea>
                        </v-col>
                        <v-col cols="12" sm="6" md="4">
                            <v-select
                                v-model="data.product.categoryCode"
                                :items="categoryOptions"
                                item-title="code"
                                item-value="code"
                                :label="formLabelText.categoryCode"
                            ></v-select>
                        </v-col>
                        <v-col cols="12" sm="6" md="4">
                            <strong>{{ formLabelText.dateCreateAt }}:</strong>
                            {{ formatDateTime(data.product.dateCreateAt) }}
                        </v-col>
                        <v-col cols="12" sm="6" md="4">
                            <strong>{{ formLabelText.dateModifyAt }}:</strong>
                            {{ formatDateTime(data.product.dateModifyAt) }}
                        </v-col>
                        <v-col cols="12">
                            <div class="d-flex justify-end">
                                <v-btn color="primary" @click="onSaveClick"> Save Changes </v-btn>
                            </div>
                        </v-col>
                    </v-row>
                    <v-row v-else>
                        <v-col cols="12">
                            <v-progress-circular indeterminate color="primary"></v-progress-circular>
                            Loading product information...
                        </v-col>
                    </v-row>
                </v-card-text>
            </v-card>

            <!-- Product Costs Section -->
            <v-card class="mb-6">
                <v-card-title>
                    <h2>Product Cost History</h2>
                </v-card-title>
                <v-card-text>
                    <v-table>
                        <thead>
                            <tr>
                                <th class="text-left" v-for="header in productCostHeaders" :key="header.value">
                                    {{ header.text }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="item in data.productCostList" :key="item.productCostId">
                                <td v-for="header in productCostHeaders" :key="header.value">
                                    <template v-if="header.value !== productCostActionHeaderItem.value">
                                        {{ formatCellValue(item, header) }}
                                    </template>
                                    <v-btn
                                        v-else-if="isActive(item)"
                                        variant="text"
                                        icon="mdi-delete"
                                        color="error"
                                        @click="onDeleteProductCostClick(item)"
                                    >
                                    </v-btn>
                                </td>
                            </tr>
                        </tbody>
                    </v-table>
                    <div v-if="!data.productCostList.length" class="text-center mt-4">
                        No product cost records found
                    </div>
                </v-card-text>
            </v-card>

            <!-- Estimated Shipping Fees Section -->
            <v-card>
                <v-card-title>
                    <h2>Estimated Shipping Fee History</h2>
                </v-card-title>
                <v-card-text>
                    <v-table>
                        <thead>
                            <tr>
                                <th class="text-left" v-for="header in estimatedShippingFeeHeaders" :key="header.value">
                                    {{ header.text }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="item in data.shippingFeeList" :key="item.estimatedShippingFeeId">
                                <td v-for="header in estimatedShippingFeeHeaders" :key="header.value">
                                    <template v-if="header.value !== shippingFeeActionHeaderItem.value">
                                        {{ formatCellValue(item, header) }}
                                    </template>
                                    <v-btn
                                        v-else-if="isActive(item)"
                                        variant="text"
                                        icon="mdi-delete"
                                        color="error"
                                        @click="onDeleteShippingFeeClick(item)"
                                    >
                                    </v-btn>
                                </td>
                            </tr>
                        </tbody>
                    </v-table>
                    <div v-if="!data.shippingFeeList.length" class="text-center mt-4">
                        No estimated shipping fee records found
                    </div>
                </v-card-text>
            </v-card>
        </v-col>
    </v-row>
</template>

<script lang="ts">
import { defineComponent, reactive, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { fetchWrapper } from '../../utils/FetchWrapper';
import { ProductLabelTextHelper } from '../../utils/labelTextHelper/ProductLabelTextHelper';
import { ProductCostLabelTextHelper } from '../../utils/labelTextHelper/ProductCostLabelTextHelper';
import { EstimatedShippingFeeLabelTextHelper } from '../../utils/labelTextHelper/EstimatedShippingFeeLabelTextHelper';
import { Formatter } from '../../utils/Formatter';
import { TableFormatHelper } from '../../utils/TableFormatHelper';
import { ProductCostHeaderHelper } from '../../utils/headerHelper/ProductCostHeaderHelper';
import { EstimatedShippingFeeHeaderHelper } from '../../utils/headerHelper/EstimatedShippingFeeHeaderHelper';
import AlertMessage from '../../components/AlertMessage.vue';
import { useMasterData } from '../../composables/useMasterData';

dayjs.extend(timezone);
dayjs.extend(advancedFormat);
dayjs.extend(utc);

export default defineComponent({
    name: 'UpdateProduct',

    components: {
        AlertMessage
    },

    setup() {
        const route = useRoute();
        const productId = route.params.productId;

        // Load master data options for dropdowns
        const { categoryOptions, loadAllMasterData } = useMasterData();

        const data = reactive({
            errorAlertMessage: '',
            product: null,
            productCostList: [],
            shippingFeeList: [],
            showDeleteProductCostDialog: false,
            productCostToDelete: null,
            showDeleteShippingFeeDialog: false,
            shippingFeeToDelete: null
        });

        onMounted(async () => {
            await Promise.all([
                loadAllMasterData(),
                loadProductDetails(),
                loadProductCosts(),
                loadEstimatedShippingFees()
            ]);
        });

        const loadProductDetails = async () => {
            try {
                const requestUrl = `/api/staff/product/getById?productId=${productId}`;
                const response = await fetchWrapper.get(requestUrl, '');

                if (response.success) {
                    data.product = response.result;
                } else {
                    data.errorAlertMessage = response.errors || 'Failed to load product details';
                    const errorAlert = document.querySelector('alert-message') as any;
                    if (errorAlert) errorAlert.showAlert();
                }
            } catch (error) {
                data.errorAlertMessage = 'An error occurred while fetching product details';
                const errorAlert = document.querySelector('alert-message') as any;
                if (errorAlert) errorAlert.showAlert();
            }
        };

        const loadProductCosts = async () => {
            try {
                const requestUrl = `/api/staff/productCost/getByProductId?productId=${productId}`;
                const response = await fetchWrapper.get(requestUrl, '');

                if (response.success) {
                    data.productCostList = response.result || [];
                } else {
                    data.errorAlertMessage = response.errors || 'Failed to load product costs';
                    const errorAlert = document.querySelector('alert-message') as any;
                    if (errorAlert) errorAlert.showAlert();
                }
            } catch (error) {
                data.errorAlertMessage = 'An error occurred while fetching product costs';
                const errorAlert = document.querySelector('alert-message') as any;
                if (errorAlert) errorAlert.showAlert();
            }
        };

        const loadEstimatedShippingFees = async () => {
            try {
                const requestUrl = `/api/staff/estimatedShippingFee/getByProductId?productId=${productId}`;
                const response = await fetchWrapper.get(requestUrl, '');

                if (response.success) {
                    data.shippingFeeList = response.result || [];
                } else {
                    data.errorAlertMessage = response.errors || 'Failed to load estimated shipping fees';
                    const errorAlert = document.querySelector('alert-message') as any;
                    if (errorAlert) errorAlert.showAlert();
                }
            } catch (error) {
                data.errorAlertMessage = 'An error occurred while fetching estimated shipping fees';
                const errorAlert = document.querySelector('alert-message') as any;
                if (errorAlert) errorAlert.showAlert();
            }
        };

        return {
            formLabelText: ProductLabelTextHelper.getLabelText,
            productCostLabelText: ProductCostLabelTextHelper.getLabelText,
            // skip sku, the first column, but include action column
            productCostHeaders: [
                ...ProductCostHeaderHelper.getHeaderItems().slice(1),
                ProductCostHeaderHelper.getActionHeaderItem()
            ],
            productCostActionHeaderItem: ProductCostHeaderHelper.getActionHeaderItem(),
            estimatedShippingFeeLabelText: EstimatedShippingFeeLabelTextHelper.getLabelText,
            estimatedShippingFeeHeaders: [
                ...EstimatedShippingFeeHeaderHelper.getHeaderItems().slice(1),
                EstimatedShippingFeeHeaderHelper.getActionHeaderItem()
            ],
            shippingFeeActionHeaderItem: EstimatedShippingFeeHeaderHelper.getActionHeaderItem(),
            categoryOptions,
            data
        };
    },

    methods: {
        formatCellValue(item: any, header: { value: string; format?: string }) {
            return TableFormatHelper.formatDateTimeCellValue(item, header);
        },
        formatDateTime(iso8601Time: string): string {
            return TableFormatHelper.formatDateTime(iso8601Time);
        },
        formatDate(value: string): string {
            return TableFormatHelper.formatDate(value);
        },
        isActive: function (item: any) {
            return item.isDeleted === false;
        },
        async onSaveClick() {
            try {
                const requestUrl = `/api/staff/product/updateById`;
                const requestData = {
                    productId: this.data.product.productId,
                    sku: this.data.product.sku,
                    title: this.data.product.title,
                    description: this.data.product.description,
                    categoryCode: this.data.product.categoryCode
                };

                const response = await fetchWrapper.post(requestUrl, requestData);

                if (response.success) {
                    this.data.errorAlertMessage = 'Product updated successfully!';
                    this.$refs.errorAlert.showAlert('success');
                } else {
                    this.data.errorAlertMessage = response.errors || 'Failed to update product';
                    this.$refs.errorAlert.showAlert();
                }
            } catch (error) {
                this.data.errorAlertMessage = 'An error occurred while updating the product';
                this.$refs.errorAlert.showAlert();
            }
        },

        // Product Cost Delete Methods
        onDeleteProductCostClick(item: any) {
            this.data.productCostToDelete = item;
            this.data.showDeleteProductCostDialog = true;
        },
        cancelDeleteProductCost() {
            this.data.showDeleteProductCostDialog = false;
            this.data.productCostToDelete = null;
        },
        async confirmDeleteProductCost() {
            try {
                const item = this.data.productCostToDelete;
                const requestUrl = `/api/staff/productCost/batchDelete`;
                const requestData = {
                    productCostIdList: [item.productCostId]
                };
                const response = await fetchWrapper.post(requestUrl, requestData);
                if (response.success) {
                    this.data.errorAlertMessage = 'Product cost record deleted successfully!';
                    this.$refs.errorAlert.showAlert('success');
                    this.data.productCostList = this.data.productCostList.filter(
                        (i) => i.productCostId !== item.productCostId
                    );
                } else {
                    this.data.errorAlertMessage = response.errors || 'Failed to delete product cost record';
                    this.$refs.errorAlert.showAlert();
                }
            } catch (error) {
                this.data.errorAlertMessage = 'An error occurred while deleting the product cost record';
                this.$refs.errorAlert.showAlert();
            } finally {
                this.data.showDeleteProductCostDialog = false;
                this.data.productCostToDelete = null;
            }
        },

        // Estimated Shipping Fee Delete Methods
        onDeleteShippingFeeClick(item: any) {
            this.data.shippingFeeToDelete = item;
            this.data.showDeleteShippingFeeDialog = true;
        },
        cancelDeleteShippingFee() {
            this.data.showDeleteShippingFeeDialog = false;
            this.data.shippingFeeToDelete = null;
        },
        async confirmDeleteShippingFee() {
            try {
                const item = this.data.shippingFeeToDelete;
                const requestUrl = `/api/staff/estimatedShippingFee/batchDelete`;
                const requestData = {
                    estimatedShippingFeeIdList: [item.estimatedShippingFeeId]
                };
                const response = await fetchWrapper.post(requestUrl, requestData);
                if (response.success) {
                    this.data.errorAlertMessage = 'Estimated shipping fee record deleted successfully!';
                    this.$refs.errorAlert.showAlert('success');
                    this.data.shippingFeeList = this.data.shippingFeeList.filter(
                        (i) => i.estimatedShippingFeeId !== item.estimatedShippingFeeId
                    );
                } else {
                    this.data.errorAlertMessage = response.errors || 'Failed to delete estimated shipping fee record';
                    this.$refs.errorAlert.showAlert();
                }
            } catch (error) {
                this.data.errorAlertMessage = 'An error occurred while deleting the estimated shipping fee record';
                this.$refs.errorAlert.showAlert();
            } finally {
                this.data.showDeleteShippingFeeDialog = false;
                this.data.shippingFeeToDelete = null;
            }
        }
    }
});
</script>

<style scoped>
.validation-error {
    color: red;
}
</style>
